import { useState } from 'react';
import { BarChart3, FileText, LayoutDashboard, TrendingUp, Wallet } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import {
  EstadoFinancieroBody,
  EstadoFinancieroToolbar,
  useEstadoFinancieroFilters,
} from '@/features/reportes/EstadoFinancieroReport';
import { FlujoPorMesPanel } from '@/features/reportes/FlujoPorMesPanel';
import { EvolucionPanel } from '@/features/reportes/EvolucionPanel';
import {
  exportEstadoFinanciero,
  exportFlujoPorMes,
} from '@/features/reportes/efExport';
import { OperativoSegment } from './OperativoSegment';

/**
 * Dashboard — dos segmentos de nivel superior (botonera arriba):
 *
 *   - Operativo    — KPIs del negocio sobre nuestros propios datos. Hoy
 *                    arranca con Ventas (AR); crece con más bloques.
 *   - Flujo de caja — el tablero financiero previo (flujo por mes /
 *                    evolución / estado financiero), que reemplazó al
 *                    reporte de Power BI embebido (2026-08-03).
 *
 * El segmento Operativo está pensado **solo para Argentina**: fuera de AR
 * se muestra un aviso en lugar de los widgets (los montos son ARS sin FX).
 */

const SEGMENTOS = [
  { id: 'operativo', label: 'Operativo', Icon: LayoutDashboard },
  { id: 'flujo-caja', label: 'Flujo de caja', Icon: Wallet },
] as const;

type SegmentoId = (typeof SEGMENTOS)[number]['id'];

export function DashboardPage() {
  const [segmento, setSegmento] = useState<SegmentoId>('operativo');

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Dashboard
        </h1>
      </div>

      {/* Botonera de segmentos */}
      <div className="flex shrink-0 flex-wrap items-center gap-1" role="tablist">
        {SEGMENTOS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={segmento === id}
            onClick={() => setSegmento(id)}
            className={cn(
              'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              segmento === id
                ? 'bg-foreground text-background'
                : 'text-muted-foreground hover:bg-muted',
            )}
          >
            <Icon className="h-4 w-4" aria-hidden />
            {label}
          </button>
        ))}
      </div>

      {segmento === 'operativo' ? <OperativoTab /> : <FlujoCajaTab />}
    </div>
  );
}

/** Segmento Operativo — gateado a Argentina. Scrollea internamente porque el
 *  shell (AppShell) fija la altura del área de contenido con overflow oculto. */
function OperativoTab() {
  const { code } = useActiveCountry();
  if (code !== 'AR') {
    return (
      <div className="flex flex-1 items-center justify-center rounded-lg border bg-card p-10 text-center">
        <p className="max-w-md text-sm text-muted-foreground">
          El tablero operativo está disponible solo para <strong>Argentina</strong>.
          Cambiá el país activo a AR para verlo.
        </p>
      </div>
    );
  }
  return (
    <div className="min-h-0 flex-1 overflow-y-auto pb-6 pr-1">
      <OperativoSegment />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flujo de caja — las tres solapas del tablero financiero previo
// ---------------------------------------------------------------------------

const SOLAPAS_FLUJO = [
  { id: 'flujo', label: 'Flujo por mes', Icon: BarChart3 },
  { id: 'evolucion', label: 'Evolución', Icon: TrendingUp },
  { id: 'estado', label: 'Estado Financiero', Icon: FileText },
] as const;

type SolapaFlujoId = (typeof SOLAPAS_FLUJO)[number]['id'];

function FlujoCajaTab() {
  // Un solo hook para las tres solapas: cambiar de solapa no vuelve a pedir
  // los datos. El filtro (período, granularidad, moneda) vive en el toolbar.
  const estadoFinanciero = useEstadoFinancieroFilters(true);
  const [solapa, setSolapa] = useState<SolapaFlujoId>('flujo');

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <div
          className="mr-auto flex flex-wrap items-center gap-1"
          role="tablist"
          aria-label="Vistas de flujo de caja"
        >
          {SOLAPAS_FLUJO.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={solapa === id}
              onClick={() => setSolapa(id)}
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                solapa === id
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted',
              )}
            >
              <Icon className="h-4 w-4" aria-hidden />
              {label}
            </button>
          ))}
        </div>
        <EstadoFinancieroToolbar
          state={estadoFinanciero}
          exportar={
            estadoFinanciero.query.data && solapa !== 'evolucion'
              ? () =>
                  solapa === 'flujo'
                    ? exportFlujoPorMes(estadoFinanciero.query.data!)
                    : exportEstadoFinanciero(estadoFinanciero.query.data!)
              : undefined
          }
        />
      </div>

      {solapa === 'flujo' ? (
        <FlujoPorMesPanel state={estadoFinanciero} />
      ) : solapa === 'evolucion' ? (
        <EvolucionPanel state={estadoFinanciero} />
      ) : (
        <EstadoFinancieroBody state={estadoFinanciero} />
      )}
    </div>
  );
}
